// HTTP-first auth flow (PROTOCOL.md §7).
//
// The flow has two tiers:
//
//   1. Lab identity. A long-lived credential that says "this caller
//      is allowed to reach lab X." Carried in `Authorization: Bearer`
//      on every /api/auth/* request. Topology decides what's inside:
//
//        - Web (Worker, today)  : the raw lab_id (stand-in).
//        - Web (Worker, future) : the user's external-IdP access
//                                 token; Worker validates the
//                                 signature against IdP JWKS and
//                                 routes by `claims.sub`.
//        - SKIF controllerd     : no bearer; controllerd validated
//                                 the license file at startup.
//        - SW (paired-tab)      : no bearer; SW doesn't gate at all.
//
//      The AuthClient doesn't care which topology it's in — callers
//      hand it a `labBearer` option (string or thunk). The shape is
//      identical across topologies; the option is the only knob.
//
//   2. Role + password. POSTed in the body of /api/auth/role; the
//      lab the request lands on (selected by the bearer above) does
//      the local check and mints the role-bound Session JWT a.k.a.
//      Controller JWT. Refresh follows the same pattern: Lab bearer
//      authorizes the request, body carries the session token to
//      rotate.
//
// Session tokens live in memory only — never localStorage — so a
// browser snapshot cannot leak credentials to disk.

import type { Role } from '@usability-suite/schema';
import { helmUserId } from './identity';

export interface AuthSession {
  role: Role;
  token: string;
  /** unix seconds when the token stops being valid */
  expiresAt: number;
}

export interface AuthError {
  status: number;
  detail: string;
}

const isAuthError = (v: unknown): v is AuthError =>
  !!v &&
  typeof v === 'object' &&
  'status' in v &&
  typeof (v as { status: unknown }).status === 'number';

export interface AuthClientOptions {
  /** Base origin for the fetch. Default: same-origin (''). */
  origin?: string;

  /**
   * Lab-tier credential to send as `Authorization: Bearer`.
   *
   * - `string`         — fixed value (rare; mostly for tests).
   * - `() => string|…` — read at call time, e.g. from URL / cookie.
   * - `undefined`      — fall back to the Web default (`lab_id`
   *                      query param off `window.location`). Pass
   *                      `null` or `() => null` explicitly for
   *                      topologies that intentionally omit the
   *                      bearer (SKIF, SW).
   */
  labBearer?: string | null | (() => string | null | undefined);
}

function defaultLabBearer(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get('lab_id');
}

function resolveLabBearer(src: AuthClientOptions['labBearer']): string | null {
  if (src === undefined) return defaultLabBearer();
  if (src === null) return null;
  if (typeof src === 'string') return src || null;
  return src() ?? null;
}

function withLabBearer(
  base: Record<string, string>,
  bearer: string | null,
): Record<string, string> {
  if (!bearer) return base;
  return { ...base, authorization: 'Bearer ' + bearer };
}

// Per-installation slug, persisted by `helmUserId()`. Reflected on
// every HTTP request so the server (controllerd, the DO, or any
// future proxy) can correlate the call to the same human across
// auth-token rotations. The header name matches the WS-side
// `node.register.payload.userId`.
function withHelmUserId(base: Record<string, string>): Record<string, string> {
  return { ...base, 'x-helm-user-id': helmUserId() };
}

export class AuthClient {
  private readonly origin: string;
  private readonly labBearer: AuthClientOptions['labBearer'];

  constructor(originOrOpts: string | AuthClientOptions = '') {
    if (typeof originOrOpts === 'string') {
      this.origin = originOrOpts;
      this.labBearer = undefined;
    } else {
      this.origin = originOrOpts.origin ?? '';
      this.labBearer = originOrOpts.labBearer;
    }
  }

  /** POST /api/auth/role; returns the AuthSession or AuthError.
   *
   *  Lab bearer goes in Authorization (today: lab_id stand-in;
   *  tomorrow: external-IdP access token). Role + password go in
   *  the body. */
  async login(role: Role, password: string): Promise<AuthSession | AuthError> {
    const headers = withHelmUserId(
      withLabBearer(
        { 'content-type': 'application/json' },
        resolveLabBearer(this.labBearer),
      ),
    );
    const res = await fetch(this.origin + '/api/auth/role', {
      method: 'POST',
      headers,
      body: JSON.stringify({ role, password }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      return { status: res.status, detail: body || res.statusText };
    }
    const json = (await res.json()) as { token: string; expiresAt: number };
    return { role, token: json.token, expiresAt: json.expiresAt };
  }

  /** POST /api/auth/refresh; rotates the session token.
   *
   *  Lab bearer in Authorization (same as login); current session
   *  token in body. Keeping the lab bearer separate from the
   *  session token in flight means the Worker can route on lab
   *  without ever touching the session JWT, and the LabDO can
   *  verify the session JWT without re-implementing IdP logic. */
  async refresh(current: AuthSession): Promise<AuthSession | AuthError> {
    const headers = withHelmUserId(
      withLabBearer(
        { 'content-type': 'application/json' },
        resolveLabBearer(this.labBearer),
      ),
    );
    const res = await fetch(this.origin + '/api/auth/refresh', {
      method: 'POST',
      headers,
      body: JSON.stringify({ token: current.token }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      return { status: res.status, detail: body || res.statusText };
    }
    const json = (await res.json()) as { token: string; expiresAt: number };
    return { role: current.role, token: json.token, expiresAt: json.expiresAt };
  }
}

export { isAuthError };
